import api from '@/lib/axios';

function unwrap<T>(r: { data: { data?: T } | T }): T {
  const body: any = r.data;
  return (body?.data ?? body) as T;
}

export interface ReportCardHeader {
  studentId: string;
  studentName: string;
  admissionNo: string;
  className: string;
  sectionName: string;
  rollNumber: number | null;
  academicYearId: string;
  academicYearName: string;
}

export interface ReportCardAttendance {
  present: number;
  absent: number;
  late: number;
  holiday: number;
  halfDay: number;
  percentage: number | null;
  band: 'green' | 'amber' | 'red' | null;
}

export interface ReportCardFeeRow {
  feeStructureName: string;
  amount: number;
  discount: number;
  paid: number;
  pending: number;
}

export interface ReportCardFees {
  demanded: number;
  paid: number;
  pending: number;
  rows: ReportCardFeeRow[];
}

export interface ReportCardAcademicPerformanceRow {
  examId: string;
  examName: string;
  totalObtained: number;
  totalMax: number;
  overallPercent: number;
  cumulativeGrade: string;
  finalPassStatus: boolean;
  publishedAt: string;
}

export interface ReportCardCoScholasticRow {
  categoryId: string;
  categoryName: string;
  term: string;
  feedbackTier: 'need_improvement' | 'basic' | 'good' | 'excellent';
}

export interface ReportCard {
  header: ReportCardHeader;
  attendance: ReportCardAttendance | null;
  fees: ReportCardFees | null;
  academicPerformance: ReportCardAcademicPerformanceRow[] | null;
  coScholastic: ReportCardCoScholasticRow[] | null;
}

export interface ReportCardPdfStatus {
  status: 'ready' | 'generating';
  pdfUrl: string | null;
  generatedAt: string | null;
}

export interface ReportCardBulkResult {
  sectionId: string;
  academicYearId: string;
  studentCount: number;
  queued: number;
}

export interface ReportCardStatusRow {
  studentId: string;
  admissionNo: string;
  rollNumber: number | null;
  studentName: string;
  pdfUrl: string | null;
  generatedAt: string | null;
}

export const ReportCardsApi = {
  get: async (studentId: string, academicYearId?: string): Promise<ReportCard> =>
    unwrap(await api.get(`/report-cards/${studentId}`, { params: academicYearId ? { academicYearId } : undefined })),

  pdf: async (studentId: string, academicYearId?: string): Promise<ReportCardPdfStatus> =>
    unwrap(await api.get(`/report-cards/${studentId}/pdf`, { params: academicYearId ? { academicYearId } : undefined })),

  bulk: async (sectionId: string, academicYearId: string): Promise<ReportCardBulkResult> =>
    unwrap(await api.post('/report-cards/bulk', null, { params: { sectionId, academicYearId } })),

  bulkStatus: async (sectionId: string, academicYearId: string): Promise<ReportCardStatusRow[]> =>
    unwrap(await api.get('/report-cards/bulk/status', { params: { sectionId, academicYearId } })),
};
